import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.products'), href: '#products' },
    { name: t('nav.partners'), href: '#partners' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gradient-to-r from-napa-blue-900 via-napa-blue-800 to-napa-blue-900 shadow-xl' 
          : 'bg-gradient-to-r from-napa-blue-900/90 via-napa-blue-800/90 to-napa-blue-900/90 backdrop-blur-md'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-18 lg:h-22">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="#home" className="flex items-center">
              <img 
                src="/image/LogoNapa.png" 
                alt="Napa Corporation Logo" 
                className="h-16 w-auto lg:h-20 min-w-[200px] transition-transform duration-300 hover:scale-105"
              />
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:block">
            <div className="-ml-2 flex items-baseline space-x-6">
              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={`px-5 py-4 rounded-lg text-base font-semibold transition-colors duration-200 font-body ${
                    isScrolled
                      ? 'text-blue-100 hover:text-white hover:bg-blue-600/30'
                      : 'text-white hover:text-blue-200 hover:bg-white/10'
                  }`}
                >
                  {item.name}
                </a>
              ))}
              
              {/* Language Toggle Button */}
              <button
                onClick={() => setLanguage(language === 'th' ? 'en' : 'th')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 border-2 ${
                  isScrolled
                    ? 'text-blue-100 border-blue-300 hover:text-white hover:bg-blue-600/30 hover:border-blue-200'
                    : 'text-white border-white/30 hover:text-blue-200 hover:bg-white/10 hover:border-white/50'
                }`}
                title={t('nav.language')}
              >
                {language === 'th' ? 'EN' : 'TH'}
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              title={isMobileMenuOpen ? t('nav.close') : t('nav.open')}
              className={`inline-flex items-center justify-center p-4 rounded-lg transition-colors duration-200 ${
                isScrolled
                  ? 'text-blue-100 hover:text-white hover:bg-blue-600/30'
                  : 'text-white hover:text-blue-200 hover:bg-white/10'
              }`}
            >
              <svg
                className="h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden">
            <div className="px-6 pt-6 pb-6 space-y-3 sm:px-8 bg-gradient-to-r from-napa-blue-800/95 to-napa-blue-900/95 backdrop-blur-md rounded-xl mt-4 shadow-lg border border-blue-600/30">
              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block px-5 py-4 rounded-lg text-lg font-semibold text-blue-100 hover:text-white hover:bg-blue-600/30 transition-colors duration-200 font-body"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
